import { useMemo, useState } from 'react';
import Locale from '@formatjs/intl-locale';
import { Button, TYPE } from '@common/Button/Button';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useIAConfigurator } from '@hooks/useIAConfigurator';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { WidenImage } from '@common/Widen/WidenImage';
import { Modal, ModalVariant } from '@common/Modal/Modal';
import { getProductIdForLocale } from '@libs/product.helper';
import type { ProductFragmentEnhanced } from '@models/enhanced-fragments';

interface InteriorSeparationConfiguratorBoxProps {
  currentProduct: ProductFragmentEnhanced;
}

export const InteriorSeparationConfiguratorBox: React.FC<InteriorSeparationConfiguratorBoxProps> = ({
  currentProduct,
}) => {
  const { locale } = useRouter();
  const chainLength = useEchainProductStore(state => state.kette?.EffektiveLaenge?.Value || 0);
  const chainId = getProductIdForLocale(currentProduct, locale);
  const { t } = useTranslation(['product/echainBuyBox']);
  const { lg } = useBreakpoints();
  const { openIaConfiguratorWindow } = useIAConfigurator(chainId, chainLength);
  const [isMobileHintModalVisible, setIsMobileHintModalVisible] = useState(false);

  const iaConfig = useEchainProductStore(store => store.eChainPartsList?.iaConfig);

  const languageCultureQuery = useMemo(() => {
    if (!locale) {
      return '';
    }
    const localeObj = new Locale(locale);
    if (!localeObj.language || !localeObj.region) {
      return '';
    }
    return `&l=${localeObj.language}&c=${localeObj.region}`;
  }, [locale]);

  const isIaConfigAvailable = Boolean(iaConfig?.configId);
  const buttonText = isIaConfigAvailable
    ? t('ECHAIN_PRODUCT_PAGE.INTERIOR_SEPARATORS.EDIT_CONFIGURATION')
    : t('ECHAIN_PRODUCT_PAGE.INTERIOR_SEPARATORS.OPEN_CONFIGURATOR');

  return (
    <div
      data-testid="interior-separation-configurator-box"
      className="bg-white ml-2 mr-2 mb-8 border border-gray-200 p-8 grid grid-cols-3"
    >
      <div className="col-span-2">
        <h3 className="mb-4 text-sm font-bold">{t('ECHAIN_PRODUCT_PAGE.INTERIOR_SEPARATORS.CONFIGURATOR_HEADLINE')}</h3>
        <p className="mb-8 text-sm">{t('ECHAIN_PRODUCT_PAGE.INTERIOR_SEPARATORS.CONFIGURATOR_TEXT')}</p>
        {lg ? (
          <Button
            onClick={() => openIaConfiguratorWindow(isIaConfigAvailable)} // if iaConfig is not undefined, pass true to edit the existing configuration instead of creating a new one}
            external
            text={buttonText}
            className="md:w-auto mb-7"
          />
        ) : (
          <>
            <Button
              type={TYPE.SECONDARY}
              onClick={() => setIsMobileHintModalVisible(true)}
              text={buttonText}
              className="md:w-auto mb-7 opacity-25"
            />
            <Modal
              variant={ModalVariant.ALERT}
              visible={isMobileHintModalVisible}
              onClose={() => setIsMobileHintModalVisible(false)}
              headline={t('ECHAIN_PRODUCT_PAGE.INTERIOR_SEPARATORS.CONFIGURATOR_MOBILE_USER_HINT_TITLE')}
            >
              {t('ECHAIN_PRODUCT_PAGE.INTERIOR_SEPARATORS.CONFIGURATOR_MOBILE_USER_HINT')}
            </Modal>
          </>
        )}
      </div>
      <div className="flex justify-end">
        <div className="w-full max-w-[240px]">
          <WidenImage
            shortCode={languageCultureQuery.indexOf('de') !== -1 ? 'zovprrkfsv' : '0awpzf3yvu'}
            filename={
              languageCultureQuery.indexOf('de') !== -1
                ? 'ECS_SCR_interior_separator_animated.gif'
                : 'ECS_SCR_interior_separator_animated_EN.gif'
            }
            layout="responsive"
            animate
            width={240}
            height={150}
          />
        </div>
      </div>
    </div>
  );
};
